import React, { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { Colors, Typography, VisuallyHidden } from '@zapier/design-system';
import { isolateComponent, labelStyles, withModifier, } from '@zapier/style-encapsulation';
import MenuCollapseButton from './MenuCollapseButton';
import InAppTopbar from './UniversalTopbar/InAppTopbar/InAppTopbar';
import OutOfAppTopbar from './UniversalTopbar/OutOfAppTopbar/OutOfAppTopbar';
import TopbarLoadingState from './TopbarLoadingState';
import InAppSidebar from './UniversalSidebar/InAppSidebar/InAppSidebar';
import OutOfAppSidebar from './UniversalSidebar/OutOfAppSidebar/OutOfAppSidebar';
import { addParamsToUrl, extractParams } from '@zapier/url-utils';
import { useUniversalNavContext, shouldRenderTopbarLinksForRoute, useQuerySession, } from '../hooks';
import findAccountsMenuRenderer from '../utils/findAccountsMenuRenderer';
import findNotificationsRenderer from '../utils/findNotificationsRenderer';
import useUnscrollableBody from '../hooks/useUnscrollableBody';
import { UNIVERSAL_NAV_ZINDEX, loginPath, signupPath, UNIVERSAL_TOPBAR_CONDENSED_HEIGHT, UNIVERSAL_TOPBAR_HEIGHT, skipLinkHref as skipLinkHrefDefault, } from '../constants';
const Styles = labelStyles('UniversalNav', {
    root: ({ isV3TopbarResponse, shouldHeaderStickToTop, }) => [
        css `
      position: sticky;
      z-index: ${UNIVERSAL_NAV_ZINDEX};
      top: -${UNIVERSAL_TOPBAR_CONDENSED_HEIGHT}px;

      @media (min-width: 450px) {
        top: -${UNIVERSAL_TOPBAR_HEIGHT}px;
      }
    `,
        isV3TopbarResponse &&
            css `
        // - Styles 'My Zaps' button: https://gitlab.com/zapier/design-systems/design-system/-/blob/main/packages/universal-layout/src/universal-nav/utils/findAccountsMenuRenderer.tsx#L47-52
        & #back-to-app {
          border-radius: 40px;
          &::before {
            border-radius: 42px;
          }
          & span {
            ${Typography.paragraph1Semibold};
            color: ${Colors.neutral100};
            line-height: 24px;
          }
        }

        // - Styles 'Sign up' button: https://gitlab.com/zapier/design-systems/design-system/-/blob/main/packages/universal-layout/src/universal-nav/components/UniversalTopbar/AccountActions.tsx#L84-91
        & #universalnav-signup {
          border-radius: 40px;
          &::before {
            border-radius: 42px;
          }
          & span {
            ${Typography.paragraph1Semibold};
            color: ${Colors.neutral100};
            line-height: 24px;
          }
        }
      `,
        shouldHeaderStickToTop &&
            withModifier('stuck-to-top', css `
          top: 0;
        `),
    ],
    forceMenuCollapseButton: css `
    margin-left: 5px;
  `,
    sidebarWrapper: ({ isFixed, isFullWidth, }) => [
        css `
      position: absolute;
      display: flex;
      align-items: stretch;
      // Push beneath UniversalTopbar border so the border is seen.
      margin-top: 1px;
      top: 100%;
      left: 0;
      height: 100vh;
      will-change: height;
      transition: height 300ms linear;
    `,
        isFixed &&
            !isFullWidth &&
            css `
        position: fixed;
        top: 0;
        bottom: 0;
        height: auto;
      `,
        isFullWidth &&
            css `
        width: 100%;
      `,
    ],
});
const copyQueryString = (source, destination) => addParamsToUrl(destination, extractParams(source), true);
const Sidebar = (props) => {
    const { shouldRenderAccessibleSidebar, setIsCreateMenuOpen } = useUniversalNavContext();
    const [isCollapsed, setIsCollapsed] = useState(true);
    const isFullWidth = props.size === 'small';
    const isActuallyCollapsed = !isFullWidth &&
        !props.isSidebarForcedOpen &&
        !props.isSidebarControlledByTopbar &&
        isCollapsed;
    const sharedProps = {
        isCollapsed: isActuallyCollapsed,
        isFullWidth,
        isShadowed: isFullWidth || (!props.isSidebarForcedOpen && !isActuallyCollapsed),
        onClick: props.onClick,
        onMouseEnter: () => {
            if (window.matchMedia('(hover: hover)').matches) {
                setIsCollapsed(false);
            }
        },
        onMouseLeave: () => {
            if (window.matchMedia('(hover: hover)').matches) {
                setIsCollapsed(true);
                setIsCreateMenuOpen(false);
            }
        },
    };
    const shouldRenderLoggedInSidebar = props.isLoggedIn &&
        props.currentAccountId &&
        (props.isOnAppPage || props.shouldRenderTopbarLinks);
    useUnscrollableBody({
        shouldAllowScroll: (!isFullWidth && !sharedProps.isShadowed) ||
            shouldRenderAccessibleSidebar,
    });
    const sidebar = shouldRenderLoggedInSidebar && props.currentAccountId ? (isolateComponent(InAppSidebar, Object.assign({}, sharedProps, { currentAccountId: props.currentAccountId, selectedHref: props.currentUrl }))) : (isolateComponent(OutOfAppSidebar, Object.assign({}, sharedProps, { callToAction: props.callToAction, currentUrl: props.currentUrl, isActuallyCollapsed: isActuallyCollapsed, isOnAppPage: props.isOnAppPage, isLoggedIn: props.isLoggedIn, loginHref: props.loginHref, outOfAppLinks: props.links, signupHref: props.signupHref, size: props.size })));
    const style = { height: props.height };
    return (isolateComponent("div", { css: Styles.sidebarWrapper({
            isFixed: props.isFixed,
            isFullWidth,
        }), style: style }, sidebar));
};
export const UniversalNav = ({ renderNotifications: renderNotificationsProp = () => null, skipLinkHref = skipLinkHrefDefault, }) => {
    var _a;
    const { canTopbarFitBackToAppButton, canForceSidebarOpen, currentAccountId, currentUrl, isInAppExperience, isLoadingTopNav, isOnAppPage, isSearchFullWidth, isSidebarForcedOpen, isSidebarControlledByTopbar, isSidebarOpenedByTopbar, mode, onToggleSidebarVisibility, setIsSidebarForcedOpen, setIsSidebarOpenedByTopbar, setIsSidebarRendered, shouldRenderCallToAction, shouldRenderSearch, shouldRenderSidebar, shouldRenderAccessibleSidebar, shouldRenderTopbarLinks, topbarSize, topNavData, } = useUniversalNavContext();
    const { data: sessionData, isLoading: isSessionDataLoading } = useQuerySession();
    const isLoggedIn = (_a = sessionData === null || sessionData === void 0 ? void 0 : sessionData.isLoggedIn) !== null && _a !== void 0 ? _a : false;
    const shouldHeaderStickToTop = shouldRenderTopbarLinksForRoute({
        currentUrl,
        mode,
    });
    useEffect(() => {
        setIsSidebarRendered(shouldRenderSidebar);
        return () => {
            setIsSidebarRendered(false);
        };
    }, [setIsSidebarRendered, shouldRenderSidebar]);
    const getLinksToRender = () => {
        if (isLoadingTopNav) {
            return [{ text: '', href: '', variant: 'link' }];
        }
        return topNavData.topBarMenu;
    };
    const linksToRender = getLinksToRender();
    const loginHref = copyQueryString(currentUrl, loginPath);
    const signupHref = copyQueryString(currentUrl, signupPath);
    const renderAccountMenu = findAccountsMenuRenderer({
        canTopbarFitBackToAppButton,
        currentAccountId,
        currentUrl,
        isLoadingLoggedIn: isSessionDataLoading,
        isLoggedIn,
        loginHref,
        signupHref,
    });
    const renderNotifications = findNotificationsRenderer({
        currentUrl,
        isLoggedIn,
        renderNotifications: renderNotificationsProp,
    });
    const renderSearchForm = shouldRenderSearch ? undefined : () => undefined;
    const [bottom, setBottom] = useState(Infinity);
    const navRef = useRef(null);
    useEffect(() => {
        const setNavBottom = () => {
            if (navRef.current) {
                setBottom(navRef.current.getBoundingClientRect().bottom);
            }
        };
        setNavBottom();
        document.addEventListener('scroll', setNavBottom, { capture: true });
        window.addEventListener('resize', setNavBottom, { capture: true });
        return () => {
            document.removeEventListener('scroll', setNavBottom, { capture: true });
            window.removeEventListener('resize', setNavBottom, { capture: true });
        };
    }, []);
    const closeSidebar = () => {
        setIsSidebarOpenedByTopbar(false);
    };
    const closeSidebarOnTriggerClick = (e) => {
        var _a;
        const target = e.target;
        const triggerIsCreateMenuButton = (_a = target.parentElement) === null || _a === void 0 ? void 0 : _a.classList.contains('sidebar-create-menu-button');
        const trigger = target.closest('a, button, label, [role="button"]');
        if (isSidebarOpenedByTopbar && !!trigger && !triggerIsCreateMenuButton) {
            closeSidebar();
        }
    };
    const renderSidebar = ({ size }) => {
        const buttonLabel = isSidebarForcedOpen ? 'Close Sidebar' : 'Open Sidebar';
        const buttonIcon = isSidebarForcedOpen ? 'formX' : 'navMenu';
        const toggleSidebarVisibilityButton = (() => {
            if (onToggleSidebarVisibility) {
                return (isolateComponent(MenuCollapseButton, { ariaExpanded: isSidebarOpenedByTopbar, icon: isSidebarOpenedByTopbar ? 'formX' : 'navMenu', label: "Menu", onClick: onToggleSidebarVisibility }));
            }
            if (canForceSidebarOpen) {
                return (isolateComponent("div", { css: Styles.forceMenuCollapseButton },
                    isolateComponent(MenuCollapseButton, { ariaExpanded: isSidebarForcedOpen, icon: buttonIcon, label: buttonLabel, onClick: () => setIsSidebarForcedOpen(!isSidebarForcedOpen) })));
            }
            return null;
        })();
        const sidebarLinksToRender = linksToRender || [];
        const sidebar = shouldRenderSidebar && (isolateComponent(Sidebar, { callToAction: shouldRenderCallToAction ? topNavData.callToAction : undefined, currentAccountId: currentAccountId, currentUrl: currentUrl, height: `calc(100vh - ${Math.max(0, bottom)}px)`, isFixed: false, isLoggedIn: isLoggedIn, isOnAppPage: isOnAppPage, isSidebarControlledByTopbar: isSidebarControlledByTopbar, isSidebarForcedOpen: isSidebarForcedOpen, links: sidebarLinksToRender, loginHref: loginHref, onClick: closeSidebarOnTriggerClick, shouldRenderTopbarLinks: shouldRenderTopbarLinks, signupHref: signupHref, size: size || topbarSize }));
        const accessibleSidebar = shouldRenderAccessibleSidebar && (isolateComponent(VisuallyHidden, null,
            isolateComponent("div", { "data-testid": "hidden-sidebar" },
                isolateComponent(Sidebar, { currentAccountId: currentAccountId, currentUrl: currentUrl, height: `calc(100vh - ${Math.max(0, bottom)}px)`, isFixed: bottom <= 0, isLoggedIn: isLoggedIn, isOnAppPage: isOnAppPage, isSidebarControlledByTopbar: isSidebarControlledByTopbar, isSidebarForcedOpen: isSidebarForcedOpen, links: sidebarLinksToRender || [], loginHref: loginHref, onClick: closeSidebarOnTriggerClick, shouldRenderTopbarLinks: shouldRenderTopbarLinks, signupHref: signupHref, size: size || topbarSize }))));
        return ((toggleSidebarVisibilityButton || sidebar) && (isolateComponent(React.Fragment, null,
            toggleSidebarVisibilityButton,
            sidebar,
            accessibleSidebar)));
    };
    const [hasMounted, setHasMounted] = useState(false);
    useEffect(() => {
        setHasMounted(true);
    }, []);
    return (isolateComponent("div", { css: Styles.root({
            isV3TopbarResponse: !!topNavData.linksInApp,
            shouldHeaderStickToTop,
        }), key: hasMounted ? 'has-mounted' : 'has-not-mounted', ref: navRef }, isLoadingTopNav ? (isolateComponent(TopbarLoadingState, { isSearchFullWidth: isSearchFullWidth, links: shouldRenderTopbarLinks ? linksToRender : [], onClick: closeSidebarOnTriggerClick, onClickOutside: closeSidebar, renderAccountMenu: renderAccountMenu, renderSearchForm: renderSearchForm, renderSidebar: renderSidebar, selectedHref: currentUrl, size: topbarSize, skipLinkHref: skipLinkHref })) : isInAppExperience ? (isolateComponent(InAppTopbar, { isSearchFullWidth: isSearchFullWidth, onClick: closeSidebarOnTriggerClick, onClickOutside: closeSidebar, renderAccountMenu: renderAccountMenu, renderNotifications: renderNotifications, renderSearchForm: renderSearchForm, renderSidebar: renderSidebar, selectedHref: currentUrl, size: topbarSize, skipLinkHref: skipLinkHref })) : (isolateComponent(OutOfAppTopbar, { isSearchFullWidth: isSearchFullWidth, links: shouldRenderTopbarLinks ? linksToRender : [], onClick: closeSidebarOnTriggerClick, onClickOutside: closeSidebar, renderAccountMenu: renderAccountMenu, renderNotifications: renderNotifications, renderSearchForm: renderSearchForm, renderSidebar: renderSidebar, selectedHref: currentUrl, size: topbarSize, skipLinkHref: skipLinkHref }))));
};
//# sourceMappingURL=UniversalNav.js.map
// @zapier/universal-layout@8.11.3 (3d6dc537c)
